<script setup>
// The scenario editor (phase 4.4, D-G6 + Q1, R2-4-6 = A): the kit's EditorDrawer instead of the old popover and the
// card's in-place edit. The title is the drawer's own field; the rest are two sections that open and close — the
// basics (when it applies) and the instruction (the woot's editor, with the tools menu). Same fields and rules as
// before (all three required, description up to 500 characters) and the same event: `save` with the scenario
// ({ id, title, description, instruction }). A section with an error opens by itself, so no error hides in a
// folded section.
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import EditorDrawer from 'dashboard/components-next/polaris/cards/EditorDrawer.vue';
import FoldingGroup from 'dashboard/components-next/polaris/cards/FoldingGroup.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';

const props = defineProps({
  // null: a new scenario
  scenario: { type: Object, default: null },
});
const emit = defineEmits(['save']);
const open = defineModel('open', { type: Boolean, default: false });

const { t } = useI18n();

const blank = () => ({ id: '', title: '', description: '', instruction: '' });
const state = reactive(blank());
const original = ref(blank());
const basicsOpen = ref(true);
const drawer = ref(null);
const instructionOpen = ref(true);

const v$ = useVuelidate(
  {
    title: { required },
    description: { required },
    instruction: { required },
  },
  state
);

watch(
  () => [open.value, props.scenario],
  ([isOpen]) => {
    if (!isOpen) return;
    const next = props.scenario
      ? {
          id: props.scenario.id,
          title: props.scenario.title || '',
          description: props.scenario.description || '',
          instruction: props.scenario.instruction || '',
        }
      : blank();
    Object.assign(state, next);
    original.value = { ...next };
    basicsOpen.value = true;
    instructionOpen.value = true;
    v$.value.$reset();
  },
  { immediate: true }
);

const dirty = computed(() =>
  ['title', 'description', 'instruction'].some(
    key => state[key] !== original.value[key]
  )
);

const descriptionError = computed(() =>
  v$.value.description.$error
    ? t('POLARIS.ASSISTANTS.SCENARIOS.ADD.NEW.FORM.DESCRIPTION.ERROR')
    : ''
);
const instructionError = computed(() =>
  v$.value.instruction.$error
    ? t('POLARIS.ASSISTANTS.SCENARIOS.ADD.NEW.FORM.INSTRUCTION.ERROR')
    : ''
);
const titleError = computed(() =>
  v$.value.title.$error
    ? t('POLARIS.ASSISTANTS.SCENARIOS.ADD.NEW.FORM.TITLE.ERROR')
    : ''
);

// The page switches rows through the drawer's discard guard (asks first when there are unsaved changes).
defineExpose({ runGuarded: action => drawer.value?.runGuarded(action) });

const onSave = () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    if (descriptionError.value) basicsOpen.value = true;
    if (instructionError.value) instructionOpen.value = true;
    return;
  }
  emit('save', { ...state });
  original.value = { ...state };
  open.value = false;
};
</script>

<template>
  <EditorDrawer
    ref="drawer"
    v-model:open="open"
    v-model:title="state.title"
    :kind="t('POLARIS.PAGES.SCENARIO_EDITOR.KIND')"
    :title-placeholder="
      t('POLARIS.ASSISTANTS.SCENARIOS.ADD.NEW.FORM.TITLE.PLACEHOLDER')
    "
    :dirty="dirty"
    :save-label="
      scenario
        ? t('POLARIS.ASSISTANTS.SCENARIOS.UPDATE.UPDATE')
        : t('POLARIS.ASSISTANTS.SCENARIOS.ADD.NEW.FORM.CREATE')
    "
    data-scenario-editor
    @save="onSave"
  >
    <p
      v-if="titleError"
      class="type-polaris-caption text-polaris-destructive"
      role="alert"
    >
      {{ titleError }}
    </p>
    <FoldingGroup
      v-model:open="basicsOpen"
      :title="t('POLARIS.PAGES.SCENARIO_EDITOR.BASICS')"
    >
      <TextArea
        v-model="state.description"
        :max-length="500"
        :label="
          t('POLARIS.ASSISTANTS.SCENARIOS.ADD.NEW.FORM.DESCRIPTION.LABEL')
        "
        :placeholder="
          t('POLARIS.ASSISTANTS.SCENARIOS.ADD.NEW.FORM.DESCRIPTION.PLACEHOLDER')
        "
        :message="descriptionError"
        :message-type="descriptionError ? 'error' : 'info'"
        show-character-count
      />
    </FoldingGroup>
    <FoldingGroup
      v-model:open="instructionOpen"
      :title="t('POLARIS.ASSISTANTS.SCENARIOS.ADD.NEW.FORM.INSTRUCTION.LABEL')"
    >
      <Editor
        v-model="state.instruction"
        :placeholder="
          t('POLARIS.ASSISTANTS.SCENARIOS.ADD.NEW.FORM.INSTRUCTION.PLACEHOLDER')
        "
        :message="instructionError"
        :message-type="instructionError ? 'error' : 'info'"
        :show-character-count="false"
        enable-polaris-tools
      />
    </FoldingGroup>
  </EditorDrawer>
</template>
